import React, { useCallback, useState } from 'react';
import { ControlLink, Field, Stack, Text, OtpInput } from '@moda/om';
import { MINUTES_TO_MILLISECONDS, noop, useForm, useTimer } from '@moda/portal-stanchions';
import { usePasswordlessAuth } from '../../hooks/usePasswordlessAuth';
import { SignInFrame } from './SignInFrame';
import { PasswordlessFrameProps } from './PasswordlessSignIn';

const PASSWORDLESS_CODE_LENGTH = 6;
const RESEND_DELAY_MS = MINUTES_TO_MILLISECONDS;

type PasswordlessOtpFormValues = {
  signInOtp: string;
};

const OTP_VALIDATIONS = {
  signInOtp: {
    required: {
      message: 'Please enter a valid 6 digit code.',
      value: true
    },
    pattern: {
      message: 'Please enter a valid 6 digit code.',
      value: /^\d{6}$/
    }
  }
};

type PasswordlessOtpStepProps = PasswordlessFrameProps & {
  email: string;
  changePayment?: () => void;
  remember?: boolean;
  onCancel: () => void;
};

export const PasswordlessOtpStep: React.FC<PasswordlessOtpStepProps> = ({
  changePayment,
  className,
  email,
  hideAuthModal,
  onAuth,
  remember = true,
  showAuthModal,
  title,
  onCancel
}) => {
  const { clearError, error, loading, resendEmail, verifyOtp } = usePasswordlessAuth();
  const [otp, setOtp] = useState('');
  const [hasResent, setHasResent] = useState(false);
  const [timeLeft] = useTimer(Date.now() + RESEND_DELAY_MS, true);

  const canResend = !hasResent && timeLeft.isOver;

  const handleOtpChange = useCallback(
    (value: string | number) => {
      if (error) clearError();
      setOtp(String(value).replace(/\D/g, '').slice(0, PASSWORDLESS_CODE_LENGTH));
    },
    [clearError, error]
  );

  const handleResend = useCallback(() => {
    if (!canResend) return;

    resendEmail({ email })
      .then(() => {
        setHasResent(true);
      })
      .catch(noop);
  }, [canResend, email, resendEmail]);

  const handleVerifySubmit = useCallback(
    (values: PasswordlessOtpFormValues) => {
      const trimmedOtp = values.signInOtp.trim();

      verifyOtp({ email, otp: trimmedOtp, remember }).then(onAuth).then(changePayment).catch(noop);
    },
    [changePayment, email, onAuth, remember, verifyOtp]
  );

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit: handleVerifySubmit,
    validations: OTP_VALIDATIONS
  });

  return (
    <SignInFrame
      className={className}
      error={error?.message}
      hideAuthModal={hideAuthModal}
      loading={loading}
      message={`We sent you a 6 digit code to your email ${email}.`}
      onAuth={onAuth}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      showAuthModal={showAuthModal}
      showCancelButton
      showSocialAuth={false}
      submitLabel="CONTINUE"
      title={title}
      footer={
        <Stack space={2} alignItems="center">
          {hasResent ? (
            <Text>The email was resent.</Text>
          ) : (
            <Text>
              Didn't receive it?{' '}
              {canResend ? (
                <ControlLink onClick={handleResend} type="button">
                  Resend
                </ControlLink>
              ) : (
                <Text treatment="bold1">
                  Resend in {timeLeft.minutesLeft}:{timeLeft.secondsLeft}
                </Text>
              )}
            </Text>
          )}
          <Text>Code expires in 5 minutes</Text>
        </Stack>
      }
    >
      <Field
        className="Authentication__otp-input"
        id="signInOtp"
        name="signInOtp"
        label="Enter Code"
        error={fieldErrors.signInOtp?.message || error != null}
        showErrorIcon={false}
      >
        <OtpInput numInputs={PASSWORDLESS_CODE_LENGTH} onChange={handleOtpChange} value={otp} />
      </Field>
    </SignInFrame>
  );
};
